'use client'

import { useAuthorization } from '@entities/authentication'
import {
  subscribesTicketEntityQueries,
  SubscribeTicketCard,
  SubscribeTicketLessonCountChip,
} from '@entities/subscribes'
import { lessonCountPerMonthSchema, paymentTypeSchema } from '@server/schemas'
import { useTranslation } from '@shared/config/i18n'
import { transformNumberEnumToStringEnum } from '@shared/libs'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useRouter, useSearchParams } from 'next/navigation'
import { match } from 'ts-pattern'
import { z } from 'zod'

import { Box, Flex, HStack, Typography, VStack } from '@podo-app/design-system-temp'
import { BusinessMainIconLottie } from '@podo/assets'

import { LECTURE_TICKETS_QUERY_PARAMS } from '../../model'

type Props = {
  lessonCountPerMonth: z.infer<typeof lessonCountPerMonthSchema>
}

export const BusinessLectureTickets = ({ lessonCountPerMonth }: Props) => {
  const t = useTranslation()
  const { bearerToken } = useAuthorization()
  const searchParams = useSearchParams()
  const router = useRouter()

  const { data: subscribeTicketsData } = useSuspenseQuery({
    ...subscribesTicketEntityQueries.businessList({
      bearerToken,
      lessonCountPerMonth: transformNumberEnumToStringEnum(lessonCountPerMonth) as NumberEnumToStringEnum<
        z.infer<typeof lessonCountPerMonthSchema>
      >,
    }),
  })

  const subscribeTickets = Array.isArray(subscribeTicketsData) ? subscribeTicketsData : []

  return (
    <VStack className="gap-8">
      <VStack className="gap-5">
        <VStack className="gap-2">
          <Typography size="h1" className="text-[28px] font-bold">
            {t('subscribeList.paymentTypeHero.businessTitle')}
          </Typography>
          <Typography size="h4" className="text-[18px]">
            {t('subscribeList.paymentTypeHero.businessLine1')}
            <br />
            {t('subscribeList.paymentTypeHero.businessLine2')}
          </Typography>
        </VStack>

        <HStack className="gap-2.5">
          <Box className="bg-gray-100 text-gray-400 px-4 py-[7px] rounded-[20px]">
            <Typography size="body1">{t('subscribeList.paymentTypeHero.businessConversation')}</Typography>
          </Box>
          <Box className="bg-gray-100 text-gray-400 px-4 py-[7px] rounded-[20px]">
            <Typography size="body1">{t('subscribeList.paymentTypeHero.oneTimePayment')}</Typography>
          </Box>
        </HStack>

        <Flex className="relative w-full h-[335px]">
          <BusinessMainIconLottie loop play className="w-full h-auto" />{' '}
        </Flex>
      </VStack>

      <VStack className="gap-4">
        <Typography size="h3">{t('subscribeList.sections.lessonCount')}</Typography>

        <SubscribeTicketLessonCountChip
          selectedLessonCount={transformNumberEnumToStringEnum(lessonCountPerMonth)}
          handleChangeLessonCount={(selectedLessonCount) => {
            const queryParams = new URLSearchParams(searchParams.toString())
            queryParams.set(LECTURE_TICKETS_QUERY_PARAMS.lessonCountPerMonth, selectedLessonCount)
            router.push(`/subscribes/tickets?${queryParams.toString()}`, {
              scroll: false,
            })
          }}
          paymentType={paymentTypeSchema.enum.BUSINESS}
        />
      </VStack>

      <VStack className="gap-4">
        <Typography size="h3">{t('subscribeList.sections.product')}</Typography>

        {subscribeTickets.map((subscribeTicket) => (
          <SubscribeTicketCard
            month={subscribeTicket.lessonMonth}
            key={subscribeTicket.id}
            subscribeTicketId={subscribeTicket.id}
            catalogTicket={subscribeTicket}
            price={subscribeTicket.originPrice}
            // descriptions={getTicketDescriptions(subscribeTicket.lessonCountPerMonth)}
            descriptions={match(subscribeTicket.lessonCountPerMonth)
              .with(8, () => [
                t('subscribeList.ticketDescriptions.monthly8Fast'),
                t('subscribeList.ticketDescriptions.materials'),
              ])
              .with(12, () => [
                t('subscribeList.ticketDescriptions.flexible'),
                t('subscribeList.ticketDescriptions.materials'),
              ])
              .with(999, () => [
                t('subscribeList.ticketDescriptions.dailyFast'),
                t('subscribeList.ticketDescriptions.materials'),
              ])
              .otherwise(() => [t('subscribeList.ticketDescriptions.materials')])}
            isBest={subscribeTicket.isBest}
          />
        ))}
      </VStack>
    </VStack>
  )
}
